import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { GitBranch, X } from 'lucide-react'
import { useTeamAccess } from '../contexts/AuthContext'
import { listProjectRules } from '../lib/projectRules'
import { cn } from '../lib/utils'

// ============================================================================
// Component: ProjectRulesNudge
//
// 2026-10-08: added alongside the pod→team UI rename work. Agents only ever
// carry a single static `project_id` (agents.project_id), set once at
// connect time in ConnectAgentModal.tsx / AgentInstallManager.tsx. That's
// the WEAKEST of the three ways a usage event gets attributed to a project
// (see private.resolve_usage_project in
// 20261002103746_project_attribution_rules.sql: project_hint, then
// project_rules, then the agent's static default, in that order) -- it's
// only a fallback for whatever a rule doesn't catch.
//
// The common case this product actually sees -- one person running one
// Claude Code / Codex instance across every repo they touch -- is exactly
// what project_rules (repo/folder glob matching) was built to solve
// automatically, per usage event, with no manual per-agent picking. But
// nothing in either connect flow ever told anyone rules exist, so every
// team defaults to the weakest signal by omission, not by choice. This nudge
// closes that gap: a one-time, dismissible pointer to the Projects page's
// "Attribution rules" section, show once per team per browser (same
// dismissal pattern as TeamDashboard.tsx's channel-invite banner --
// localStorage, guarded in case it's unavailable).
//
// Admin-only: project_rules is admin-write / manager-read-only / member-no-
// access (see projectRules.ts's RLS note), and only an admin can actually DO
// anything about this nudge, so managers and members never see it.
//
// Auto-hides once the team already has >= 1 rule, independent of the
// dismissal flag -- once someone's set this up, there's nothing left to
// nudge about, and a stale "set this up" prompt sitting around would read
// as broken, not helpful.
// ============================================================================

interface ProjectRulesNudgeProps {
  teamId: string
  className?: string
}

const dismissalKey = (teamId: string) => `project_rules_nudge_dismissed_${teamId}`

export const ProjectRulesNudge: React.FC<ProjectRulesNudgeProps> = ({ teamId, className }) => {
  const { role } = useTeamAccess(teamId)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (role !== 'admin' && role !== 'owner') {
      setVisible(false)
      return
    }

    let cancelled = false
    let dismissed = false
    try {
      dismissed = localStorage.getItem(dismissalKey(teamId)) === 'true'
    } catch (storageError) {
      // If localStorage is unavailable/blocked, fail open to showing the
      // nudge rather than throwing -- same tradeoff TeamDashboard.tsx makes
      // for its own dismissible banner.
      console.error('Could not read project-rules nudge dismissal:', storageError)
    }
    if (dismissed) return

    listProjectRules(teamId)
      .then((rules) => {
        if (!cancelled) setVisible(rules.length === 0)
      })
      .catch((err) => {
        console.error('Could not check existing project rules:', err)
      })

    return () => {
      cancelled = true
    }
  }, [teamId, role])

  const dismiss = () => {
    setVisible(false)
    try {
      localStorage.setItem(dismissalKey(teamId), 'true')
    } catch (storageError) {
      console.error('Could not persist project-rules nudge dismissal:', storageError)
    }
  }

  if (!visible) return null

  return (
    <div
      className={cn(
        'flex items-start gap-3 border border-border bg-muted/40 p-3 text-sm',
        className,
      )}
    >
      <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-foreground">
          If people on your team run one connector across several projects, a project rule splits its
          usage automatically by repo or folder -- no per-connector picking needed.
        </p>
        <Link
          to={`/dashboard/team/${teamId}/projects#attribution-rules`}
          className="mt-1 inline-block font-medium text-foreground underline underline-offset-2 hover:text-foreground/80"
        >
          Set up a rule
        </Link>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

export default ProjectRulesNudge
